import { CTABand } from "../../components/site/CTABand";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { EnquiryForm } from "../../components/EnquiryForm";
import { KeralaMap } from "../../components/KeralaMap";
import { Reveal } from "../../components/motion/Reveal";
import { openEnquiryChat } from "../../components/site/EnquiryChatModal";
import { CONTACT } from "../../data/content";

const CHANNELS = [
  { icon: Phone, label: "Customer Care", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, note: `${CONTACT.phone2} · ${CONTACT.hours}`, testid: "contact-phone" },
  { icon: Mail, label: "Sales Enquiries", value: CONTACT.sales, href: `mailto:${CONTACT.sales}`, note: "New projects & site assessments", testid: "contact-sales" },
  { icon: Mail, label: "Support", value: CONTACT.support, href: `mailto:${CONTACT.support}`, note: "Existing customers & AMC", testid: "contact-support" },
  { icon: MessageCircle, label: "Chat With Us", value: "Chat with our team", action: "chat", note: `Text only · ${CONTACT.hours}`, testid: "contact-live-chat" },
];

export default function Contact() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="contact-page">
      <SiteHeader />
      <PageHero
        crumb="Contact"
        eyebrow="Contact"
        title="Talk to us —"
        titleAccent="directly, or in writing."
        description="Based in Ernakulam, serving all of Kerala. Call, email or live chat — or send the form and we'll come to you."
      />
      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="nx-wrap">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="contact-channels">
            {CHANNELS.map((c, i) => {
              const Icon = c.icon;
              if (c.action === "chat") {
                return (
                  <button key={c.testid} onClick={openEnquiryChat} className="group bg-nx-navy hover:bg-nx-navy2 p-7 transition-colors text-left" data-testid={c.testid}>
                    <Icon size={22} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
                    <p className="text-[10px] tracking-[0.26em] uppercase text-slate-500 mb-2">{c.label}</p>
                    <p className="text-sm text-white font-medium break-all">{c.value}</p>
                    <p className="text-xs text-slate-500 mt-2">{c.note}</p>
                  </button>
                );
              }
              return (
                <a key={c.testid} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group bg-nx-navy hover:bg-nx-navy2 p-7 transition-colors" data-testid={c.testid}>
                  <Icon size={22} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
                  <p className="text-[10px] tracking-[0.26em] uppercase text-slate-500 mb-2">{c.label}</p>
                  <p className="text-sm text-white font-medium break-all">{c.value}</p>
                  <p className="text-xs text-slate-500 mt-2">{c.note}</p>
                </a>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col sm:flex-row gap-6 sm:items-center border border-white/10 bg-white/[0.03] p-7" data-testid="contact-office">
              <MapPin size={22} className="text-nx-goldlight shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm text-white font-medium leading-relaxed">
                  {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
                </p>
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-2"><Clock size={13} className="text-nx-goldlight" /> {CONTACT.hours} · Contractual support hours per agreement</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 border border-nx-gold/25 p-2" data-testid="contact-map">
              <iframe
                title="NexSecure Technologies office location map"
                src="https://maps.google.com/maps?cid=16509529196170856688&z=17&output=embed"
                className="w-full h-72 sm:h-96 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <p className="mt-3 text-[10px] tracking-[0.24em] uppercase text-slate-500">Kathrikadavu, Kaloor, Ernakulam — opposite Praxis Health Technologies</p>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=NexSecure+Technologies&destination_place_id=ChIJTdY9BzANCDsR8Ei0KlWhHeU"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-directions-link"
              className="mt-4 inline-block border border-nx-gold/50 hover:border-nx-goldlight text-nx-goldlight text-xs tracking-[0.18em] uppercase px-6 py-3 transition-colors"
            >
              Get Directions
            </a>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.5fr_1fr] gap-14">
          <div>
            <Reveal>
              <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Write to Us</span>
              <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-3">Send an enquiry.</h2>
              <p className="text-base text-slate-400 mb-10 max-w-lg leading-relaxed">One solution area, one short message. Our team responds within one business day.</p>
            </Reveal>
            <Reveal delay={0.15}>
              <EnquiryForm tone="dark" source="contact" />
            </Reveal>
          </div>
          <Reveal delay={0.2} className="flex flex-col items-center lg:items-end gap-8">
            <div className="relative self-center lg:self-end">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgb(var(--nx-goldlight)/0.08),transparent_65%)]" />
              <KeralaMap tone="blue" className="relative h-[340px] sm:h-[400px] w-auto" />
              <p className="text-center text-[10px] tracking-[0.24em] uppercase text-slate-500 mt-2">On-site across all 14 districts</p>
            </div>
            <div className="relative w-full max-w-sm">
              <div className="absolute -inset-3 border border-nx-gold/25 pointer-events-none" />
              <div className="nx-clip overflow-hidden">
                <img src="https://images.unsplash.com/photo-1604328698692-f76ea9498e76?auto=format&fit=crop&w=800&q=70" alt="Modern Indian office workspace" className="w-full h-48 object-cover" loading="lazy" data-testid="contact-image" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
